import React, { useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import { IAChatProvider, useIAChat } from '../../contexts/IAChatContext.jsx';
import ChatPanel from './ChatPanel.jsx';
import ConversationList from './ConversationList.jsx';

/**
 * Burbuja flotante global del Asistente IA — puerto de
 * `IbuFront/src/components/ia/AsistenteIAWidget.jsx` (V1). Se monta desde
 * `AppLayout.jsx` (fuera de las rutas de negocio, visible en toda la app protegida),
 * igual que V1 lo montaba una sola vez en `src/index.js`.
 *
 * Gateo de permiso: `hasPermiso('asistente-ia:usar')` — si el usuario NO tiene el
 * permiso, el componente completo regresa `null` ANTES de montar `IAChatProvider`,
 * así que nunca se dispara `listarConversaciones` ni ninguna llamada al backend que
 * de todas formas respondería 403 (ver tarea, punto 5).
 *
 * En la página dedicada (`/asistente-ia`) el widget se oculta: esa página ya muestra
 * el chat a pantalla completa (mismo criterio que V1 con `enPaginaDedicada`).
 */
function ChatWidgetInner() {
  const { isOpen, toggle } = useIAChat();
  const [view, setView] = useState('chat'); // 'chat' | 'history'

  return (
    <>
      {isOpen && (
        <div
          className="fixed card overflow-hidden flex flex-col"
          style={{ bottom: 96, right: 24, width: 380, maxWidth: 'calc(100vw - 3rem)', height: 560, maxHeight: '70vh', zIndex: 1050 }}
        >
          {view === 'history' ? (
            <ConversationList onAfterSelect={() => setView('chat')} onAfterNew={() => setView('chat')} />
          ) : (
            <ChatPanel onShowHistory={() => setView('history')} />
          )}
        </div>
      )}
      <button
        type="button"
        onClick={toggle}
        title="Asistente IA"
        className="fixed flex items-center justify-center rounded-full border-0 shadow-lg bg-primary-600 text-white hover:bg-primary-700"
        style={{ bottom: 24, right: 24, width: 56, height: 56, zIndex: 1050 }}
      >
        {isOpen ? <X size={24} /> : <Sparkles size={24} />}
      </button>
    </>
  );
}

export default function ChatWidget() {
  const { isAuth, hasPermiso } = useContext(AuthContext);
  const location = useLocation();

  const enPaginaDedicada = location.pathname.toLowerCase() === '/asistente-ia';
  if (!isAuth || !hasPermiso('asistente-ia:usar') || enPaginaDedicada) return null;

  return (
    <IAChatProvider>
      <ChatWidgetInner />
    </IAChatProvider>
  );
}
